import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { QuoteLock } from 'api/quote-marketplace';

export interface QuoteLockState {
  isLocked: boolean;
  policyUuid: string | null;
  submissionId: number | null;
  lockedAt: string | null;
  policyUrl: string | null;
  isViewOnly: boolean;
  brokerName: string | null;
}

const UNLOCKED: QuoteLockState = {
  isLocked: false,
  policyUuid: null,
  submissionId: null,
  lockedAt: null,
  policyUrl: null,
  isViewOnly: false,
  brokerName: null,
};

export const buildPolicyUrl = (
  submissionId: number | null,
  policyUuid: string | null,
  section: 'personal' | 'validations' = 'personal',
): string | null => {
  if (!submissionId || !policyUuid) return null;
  return `/admin/policies/submission/${submissionId}/${policyUuid}/${section}`;
};

export const isQuoteReadOnly = (lock: QuoteLock | null | undefined) =>
  !!lock && (lock.is_locked || !lock.can_edit);

export const QuoteLockContext = createContext<QuoteLockState>(UNLOCKED);

interface QuoteLockProviderProps {
  lock: QuoteLock | null | undefined;
  children: ReactNode;
}

export const QuoteLockProvider = ({
  lock,
  children,
}: QuoteLockProviderProps) => {
  const value = useMemo<QuoteLockState>(() => {
    if (!lock || !isQuoteReadOnly(lock)) return UNLOCKED;
    return {
      isLocked: true,
      policyUuid: lock.policy_uuid,
      submissionId: lock.submission_id,
      lockedAt: lock.locked_at,
      policyUrl: buildPolicyUrl(lock.submission_id, lock.policy_uuid),
      isViewOnly: !lock.is_locked,
      brokerName: lock.broker_name,
    };
  }, [lock]);

  return (
    <QuoteLockContext.Provider value={value}>
      {children}
    </QuoteLockContext.Provider>
  );
};

export const useQuoteLock = (): QuoteLockState => useContext(QuoteLockContext);
